import React from 'react';
import { motion } from 'framer-motion';

const FAPLProjectDetails = ({ project }) => {
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
      {/* Challenges & Highlights - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Project Challenges */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-red-400">🔴 Challenges</h3>
          <div className="space-y-2">
            {project.details.challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{challenge}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Project Highlights */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-green-400">🟢 Highlights</h3>
          <div className="space-y-2">
            {project.details.highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{highlight}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* AI-Powered Development */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4 text-purple-400">🤖 AI-Powered Development</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-purple-500/10 rounded-lg p-4 text-center"
          >
            <div className="text-3xl font-bold text-purple-400 mb-1">50%</div>
            <div className="text-sm text-gray-300">Faster Development</div>
            <div className="text-xs text-gray-400 mt-1">AI-assisted coding & review</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-pink-500/10 rounded-lg p-4 text-center"
          >
            <div className="text-3xl font-bold text-pink-400 mb-1">1 Month</div>
            <div className="text-sm text-gray-300">Development Time</div>
            <div className="text-xs text-gray-400 mt-1">Including intern training</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-500/10 rounded-lg p-4 text-center"
          >
            <div className="text-3xl font-bold text-blue-400 mb-1">100%</div>
            <div className="text-sm text-gray-300">Complete API Docs</div>
            <div className="text-xs text-gray-400 mt-1">Comprehensive documentation</div>
          </motion.div>
        </div>
      </div>

      {/* System Architecture */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4 text-center">🏗️ System Architecture</h3>
        <div className="relative rounded-lg overflow-hidden border-2 border-blue-500/30 bg-gray-900/50">
          <img
            src="/pic/fapl/structure.webp"
            alt="FAPL System Architecture"
            className="w-full h-auto object-contain"
            onError={(e) => {
              e.target.src = '/pic/fapl/structure.png'; // Fallback to PNG if WebP not supported
            }}
          />
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-center">
            <div className="text-blue-400 font-bold">RabbitMQ</div>
            <div className="text-gray-400">Message Queue</div>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded p-2 text-center">
            <div className="text-green-400 font-bold">S3 Bucket</div>
            <div className="text-gray-400">Data Storage</div>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded p-2 text-center">
            <div className="text-purple-400 font-bold">AES Encryption</div>
            <div className="text-gray-400">Field-Level Security</div>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded p-2 text-center">
            <div className="text-orange-400 font-bold">Async API</div>
            <div className="text-gray-400">Non-blocking</div>
          </div>
        </div>
      </div>

      {/* Processing Flow */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4 text-center">⚙️ Processing Flow with Retry Mechanism</h3>
        <div className="relative rounded-lg overflow-hidden border-2 border-cyan-500/30 bg-gray-900/50">
          <img
            src="/pic/fapl/processing_flow.webp"
            alt="Processing Flow"
            className="w-full h-auto object-contain"
          />
        </div>
        <div className="mt-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-4">
          <h4 className="text-sm font-bold text-cyan-400 mb-3">🔄 Smart Retry Mechanism</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-cyan-400 font-bold">1</div>
              <div>
                <div className="text-gray-200 font-medium">1st Retry</div>
                <div className="text-gray-400">Wait 30s before retry</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-cyan-400 font-bold">2</div>
              <div>
                <div className="text-gray-200 font-medium">2nd Retry</div>
                <div className="text-gray-400">Update failure count</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-red-400 font-bold">3</div>
              <div>
                <div className="text-gray-200 font-medium">Final Failure</div>
                <div className="text-gray-400">Email alert sent</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Security */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-3 text-purple-400">🔒 Data Security & Encryption</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-purple-500/5 p-3 rounded-lg">
              <span className="text-purple-400 text-lg">🔐</span>
              <div>
                <div className="text-sm font-bold text-white">AES Encryption</div>
                <div className="text-xs text-gray-400">Field-level encryption</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-purple-500/5 p-3 rounded-lg">
              <span className="text-purple-400 text-lg">📋</span>
              <div>
                <div className="text-sm font-bold text-white">Sensitive Fields</div>
                <div className="text-xs text-gray-400">Name, NRIC, DOB...</div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-purple-500/5 p-3 rounded-lg">
              <span className="text-purple-400 text-lg">📧</span>
              <div>
                <div className="text-sm font-bold text-white">Alert System</div>
                <div className="text-xs text-gray-400">Email on final failure</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-purple-500/5 p-3 rounded-lg">
              <span className="text-purple-400 text-lg">⏱️</span>
              <div>
                <div className="text-sm font-bold text-white">Retry Interval</div>
                <div className="text-xs text-gray-400">30s between retries</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-3 text-green-400">📊 Project Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-bold text-white mb-2">Before This Solution</h4>
            <ul className="space-y-1 text-xs text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Manual login required for each query</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>Time-consuming process</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">✗</span>
                <span>No batch processing capability</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold text-white mb-2">After This Solution</h4>
            <ul className="space-y-1 text-xs text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>One-time batch synchronization</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Async processing with status tracking</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Automatic retry mechanism</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Enhanced data security</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAPLProjectDetails;
